import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import type { Node } from 'reactflow';
import type { FlowActionPersistedData } from './actionWorkflowTypes';
import {
  buildStandardActionOutputs,
  newRowId,
  slugifyActionNodeName,
  validateAction,
} from './actionWorkflowTypes';
import './ActionConfigPanel.css';
import { WorkflowConfigPanelMaximizeButton } from './WorkflowConfigPanelFrame';
import { MUSTACHE_SUGGEST_CN_ACTION, MustacheSuggestTextarea } from './mustacheTextareaSuggest';
import { patchFromSendEmailTemplate, SEND_EMAIL_SAMPLE_TEMPLATES } from './sendEmailTemplates';

type ActionConfigPanelProps = {
  node: Node<FlowActionPersistedData>;
  panelMaximized?: boolean;
  onTogglePanelMaximize?: () => void;
  upstreamHints: string[];
  onUpdate: (nodeId: string, patch: Partial<FlowActionPersistedData>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
};

export default function ActionConfigPanel({
  node,
  panelMaximized = false,
  onTogglePanelMaximize,
  upstreamHints,
  onUpdate,
  onDelete,
  onClose,
}: ActionConfigPanelProps) {
  const d = node.data;
  const v = useMemo(() => validateAction(d), [d]);
  const outputs = useMemo(() => buildStandardActionOutputs(d.nodeName), [d.nodeName]);
  const httpBodySuggestions = useMemo(
    () =>
      Array.from(
        new Set([
          '{{input}}',
          '{{trigger.payload}}',
          '{{trigger.variables}}',
          '{{trigger.timestamp}}',
          ...upstreamHints,
          ...buildStandardActionOutputs(d.nodeName).split('\n').filter(Boolean),
        ])
      ),
    [upstreamHints, d.nodeName]
  );
  const htmlViewerSuggestions = httpBodySuggestions;
  const [emailAdvOpen, setEmailAdvOpen] = useState(false);

  const patch = useCallback((p: Partial<FlowActionPersistedData>) => onUpdate(node.id, p), [node.id, onUpdate]);

  const insertVar = useCallback(
    (token: string) => {
      const el = document.activeElement;
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) {
        window.prompt('Copy variable:', token);
        return;
      }
      const pk = el.getAttribute('data-patch-key');
      const li = el.getAttribute('data-line-index');
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;

      const patchLineArray = (field: 'emailToLines' | 'emailCcLines' | 'emailBccLines', idx: number) => {
        const arr = [...d[field]];
        const cur = arr[idx] ?? '';
        const next = cur.slice(0, start) + token + cur.slice(end);
        arr[idx] = next;
        patch({ [field]: arr } as Partial<FlowActionPersistedData>);
        queueMicrotask(() => {
          input.focus();
          const pos = start + token.length;
          input.setSelectionRange(pos, pos);
        });
      };

      if (pk === 'emailToLines' && li !== null) {
        patchLineArray('emailToLines', parseInt(li, 10));
        return;
      }
      if (pk === 'emailCcLines' && li !== null) {
        patchLineArray('emailCcLines', parseInt(li, 10));
        return;
      }
      if (pk === 'emailBccLines' && li !== null) {
        patchLineArray('emailBccLines', parseInt(li, 10));
        return;
      }
      if (pk === 'emailAttach' && li !== null) {
        const idx = parseInt(li, 10);
        const arr = [...d.emailAttachments];
        const cur = arr[idx] ?? '';
        const next = cur.slice(0, start) + token + cur.slice(end);
        arr[idx] = next;
        patch({ emailAttachments: arr });
        queueMicrotask(() => {
          input.focus();
          const pos = start + token.length;
          input.setSelectionRange(pos, pos);
        });
        return;
      }

      const fi = el.getAttribute('data-field-index');
      if (pk === 'crmFieldValue' && fi !== null) {
        const idx = parseInt(fi, 10);
        const rows = [...d.crmFields];
        const cur = rows[idx]?.value ?? '';
        const next = cur.slice(0, start) + token + cur.slice(end);
        if (rows[idx]) rows[idx] = { ...rows[idx], value: next };
        patch({ crmFields: rows });
        queueMicrotask(() => {
          input.focus();
          input.setSelectionRange(start + token.length, start + token.length);
        });
        return;
      }
      if (pk === 'createFieldValue' && fi !== null) {
        const idx = parseInt(fi, 10);
        const rows = [...d.createFields];
        const cur = rows[idx]?.value ?? '';
        const next = cur.slice(0, start) + token + cur.slice(end);
        if (rows[idx]) rows[idx] = { ...rows[idx], value: next };
        patch({ createFields: rows });
        queueMicrotask(() => {
          input.focus();
          input.setSelectionRange(start + token.length, start + token.length);
        });
        return;
      }
      if (pk === 'httpHeaderValue' && fi !== null) {
        const idx = parseInt(fi, 10);
        const rows = [...d.httpHeaders];
        const cur = rows[idx]?.value ?? '';
        const next = cur.slice(0, start) + token + cur.slice(end);
        if (rows[idx]) rows[idx] = { ...rows[idx], value: next };
        patch({ httpHeaders: rows });
        queueMicrotask(() => {
          input.focus();
          input.setSelectionRange(start + token.length, start + token.length);
        });
        return;
      }

      if (pk && typeof (d as Record<string, unknown>)[pk] === 'string') {
        const val = String((d as unknown as Record<string, unknown>)[pk]);
        const next = val.slice(0, start) + token + val.slice(end);
        patch({ [pk]: next } as Partial<FlowActionPersistedData>);
        queueMicrotask(() => {
          input.focus();
          const pos = start + token.length;
          input.setSelectionRange(pos, pos);
        });
        return;
      }

      window.prompt('Copy variable:', token);
    },
    [d, patch]
  );

  const setLines = (key: 'emailToLines' | 'emailCcLines' | 'emailBccLines', lines: string[]) =>
    patch({ [key]: lines } as Partial<FlowActionPersistedData>);

  const addLine = (key: 'emailToLines' | 'emailCcLines' | 'emailBccLines') => {
    const cur = d[key];
    setLines(key, [...cur, '']);
  };

  const updateLine = (key: 'emailToLines' | 'emailCcLines' | 'emailBccLines', i: number, val: string) => {
    const cur = [...d[key]];
    cur[i] = val;
    setLines(key, cur);
  };

  const removeLine = (key: 'emailToLines' | 'emailCcLines' | 'emailBccLines', i: number) => {
    const cur = d[key].filter((_, j) => j !== i);
    setLines(key, cur.length ? cur : ['']);
  };

  const httpPrettify = () => {
    try {
      const o = JSON.parse(d.httpBody || '{}');
      patch({ httpBody: JSON.stringify(o, null, 2) });
    } catch {
      window.alert('Body is not valid JSON.');
    }
  };

  const renderStringList = (
    label: string,
    key: 'emailToLines' | 'emailCcLines' | 'emailBccLines',
    required?: boolean
  ) => (
    <div className="workflow-action-panel__section">
      <label className={`workflow-action-panel__label${required ? ' workflow-action-panel__label--req' : ''}`}>
        {label}
      </label>
      {d[key].map((line, i) => (
        <div key={`${key}-${i}`} className="workflow-action-panel__row">
          <input
            data-patch-key={key}
            data-line-index={i}
            className="workflow-action-panel__input"
            value={line}
            onChange={(e) => updateLine(key, i, e.target.value)}
          />
          <button type="button" className="workflow-action-panel__btn-icon" onClick={() => addLine(key)} title="Add">
            +
          </button>
          {d[key].length > 1 ? (
            <button
              type="button"
              className="workflow-action-panel__btn-icon"
              onClick={() => removeLine(key, i)}
              title="Remove"
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );

  const renderKv = (
    label: string,
    rows: { id: string; key: string; value: string }[],
    onChange: (next: { id: string; key: string; value: string }[]) => void,
    valuePatchKind: 'crmFieldValue' | 'createFieldValue' | 'httpHeaderValue'
  ) => (
    <div className="workflow-action-panel__section">
      <div className="workflow-action-panel__section-title">{label}</div>
      {rows.map((row, rowIndex) => (
        <div key={row.id} className="workflow-action-panel__kv">
          <input
            className="workflow-action-panel__input"
            placeholder="Key"
            value={row.key}
            onChange={(e) =>
              onChange(rows.map((r) => (r.id === row.id ? { ...r, key: e.target.value } : r)))
            }
          />
          <input
            className="workflow-action-panel__input"
            placeholder="Value"
            value={row.value}
            data-patch-key={valuePatchKind}
            data-field-index={rowIndex}
            onChange={(e) =>
              onChange(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
            }
          />
          <button type="button" className="workflow-action-panel__btn-icon" onClick={() => onChange(rows.filter((r) => r.id !== row.id))}>
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="workflow-action-panel__collapse-btn"
        onClick={() => onChange([...rows, { id: newRowId(), key: '', value: '' }])}
      >
        + Add
      </button>
    </div>
  );

  const kind = d.actionKind;
  const title = d.label;

  let body: React.ReactNode = null;

  if (kind === 'send-email') {
    const emailTemplateValue = d.emailTemplateId?.trim() ? d.emailTemplateId : '';
    const clearEmailTemplate = (): Partial<FlowActionPersistedData> =>
      d.emailTemplateId ? { emailTemplateId: '' } : {};

    body = (
      <>
        {renderStringList('To', 'emailToLines', true)}
        {renderStringList('CC', 'emailCcLines')}
        {renderStringList('BCC', 'emailBccLines')}
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">Content</div>
          <label className="workflow-action-panel__label">Template</label>
          <p className="workflow-action-panel__hint">
            Choose a sample to fill subject and body, then edit the fields below.
          </p>
          <select
            className="workflow-action-panel__select workflow-action-panel__select--template"
            value={emailTemplateValue}
            onChange={(e) => {
              const id = e.target.value;
              if (!id) {
                patch({ emailTemplateId: '' });
                return;
              }
              const applied = patchFromSendEmailTemplate(id);
              if (applied) patch(applied as Partial<FlowActionPersistedData>);
            }}
          >
            <option value="">Custom (no template)</option>
            {SEND_EMAIL_SAMPLE_TEMPLATES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Subject</label>
          <input
            data-patch-key="emailSubject"
            className="workflow-action-panel__input"
            value={d.emailSubject}
            onChange={(e) => patch({ emailSubject: e.target.value, ...clearEmailTemplate() })}
          />
          <label className="workflow-action-panel__label">Body</label>
          <textarea
            data-patch-key="emailBody"
            className="workflow-action-panel__textarea"
            value={d.emailBody}
            onChange={(e) => patch({ emailBody: e.target.value, ...clearEmailTemplate() })}
          />
          <div className="workflow-action-panel__btn-row">
            <select
              className="workflow-action-panel__select"
              value={d.emailBodyFormat}
              onChange={(e) =>
                patch({
                  emailBodyFormat: e.target.value as 'plain' | 'html',
                  ...clearEmailTemplate(),
                })
              }
            >
              <option value="plain">Plain Text</option>
              <option value="html">HTML</option>
            </select>
          </div>
        </div>
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">Attachments</div>
          {d.emailAttachments.map((a, i) => (
            <div key={i} className="workflow-action-panel__row">
              <input
                className="workflow-action-panel__input"
                data-patch-key="emailAttach"
                data-line-index={i}
                value={a}
                onChange={(e) => {
                  const next = [...d.emailAttachments];
                  next[i] = e.target.value;
                  patch({ emailAttachments: next });
                }}
              />
              <button
                type="button"
                className="workflow-action-panel__btn-icon"
                onClick={() => patch({ emailAttachments: d.emailAttachments.filter((_, j) => j !== i) })}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="workflow-action-panel__collapse-btn"
            onClick={() => patch({ emailAttachments: [...d.emailAttachments, ''] })}
          >
            + Add attachment
          </button>
        </div>
        <div className="workflow-action-panel__collapse">
          <button type="button" className="workflow-action-panel__collapse-btn" onClick={() => setEmailAdvOpen((o) => !o)}>
            {emailAdvOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Advanced
          </button>
          {emailAdvOpen && (
            <div className="workflow-action-panel__section">
              <label className="workflow-action-panel__label">Reply-to</label>
              <input
                className="workflow-action-panel__input"
                value={d.emailReplyTo}
                onChange={(e) => patch({ emailReplyTo: e.target.value })}
              />
            </div>
          )}
        </div>
      </>
    );
  } else if (kind === 'http-request') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">Method</label>
          <div className="workflow-action-panel__method-toggle">
            {(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={`workflow-action-panel__method-btn${d.httpMethod === m ? ' workflow-action-panel__method-btn--on' : ''}`}
                onClick={() => patch({ httpMethod: m })}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">URL</label>
          <input
            data-patch-key="httpUrl"
            className="workflow-action-panel__input"
            value={d.httpUrl}
            onChange={(e) => patch({ httpUrl: e.target.value })}
          />
        </div>
        {renderKv('Headers', d.httpHeaders, (next) => patch({ httpHeaders: next }), 'httpHeaderValue')}
        {['POST', 'PUT', 'PATCH'].includes(d.httpMethod) && (
          <div className="workflow-action-panel__section">
            <label className="workflow-action-panel__label">Body</label>
            <p className="workflow-action-panel__hint">
              Type <kbd>{'{{'}</kbd> for suggestions from upstream outputs and common variables.
            </p>
            <MustacheSuggestTextarea
              resetKey={node.id}
              id={`http-body-${node.id}`}
              data-patch-key="httpBody"
              className="workflow-action-panel__textarea"
              rows={10}
              value={d.httpBody}
              onChange={(next) => patch({ httpBody: next })}
              suggestions={httpBodySuggestions}
              classNames={MUSTACHE_SUGGEST_CN_ACTION}
              spellCheck={false}
              autoComplete="off"
            />
            <div className="workflow-action-panel__btn-row">
              <select
                className="workflow-action-panel__select"
                value={d.httpBodyFormat}
                onChange={(e) => patch({ httpBodyFormat: e.target.value as 'json' | 'text' })}
              >
                <option value="json">JSON</option>
                <option value="text">Text</option>
              </select>
              <button type="button" className="workflow-action-panel__chip" onClick={httpPrettify}>
                Prettify
              </button>
            </div>
          </div>
        )}
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">Response</div>
          <label className="workflow-action-panel__label">Timeout (ms)</label>
          <input
            type="number"
            min={500}
            max={30000}
            className="workflow-action-panel__input"
            value={d.httpTimeoutMs}
            onChange={(e) => patch({ httpTimeoutMs: Math.min(30000, Math.max(500, Number(e.target.value) || 5000)) })}
          />
          <label className="workflow-action-panel__label">
            <input
              type="checkbox"
              checked={d.httpRetry}
              onChange={(e) => patch({ httpRetry: e.target.checked })}
            />{' '}
            Retry on fail
          </label>
          {d.httpRetry && (
            <input
              type="number"
              min={1}
              max={10}
              className="workflow-action-panel__input"
              style={{ marginTop: 6 }}
              value={d.httpRetryCount}
              onChange={(e) => patch({ httpRetryCount: Number(e.target.value) || 1 })}
            />
          )}
        </div>
      </>
    );
  } else if (kind === 'send-slack') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Channel</label>
          <input
            data-patch-key="slackChannel"
            className="workflow-action-panel__input"
            value={d.slackChannel}
            onChange={(e) => patch({ slackChannel: e.target.value })}
          />
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Message</label>
          <textarea
            data-patch-key="slackMessage"
            className="workflow-action-panel__textarea"
            value={d.slackMessage}
            onChange={(e) => patch({ slackMessage: e.target.value })}
          />
          <div className="workflow-action-panel__btn-row">
            <select
              className="workflow-action-panel__select"
              value={d.slackFormat}
              onChange={(e) => patch({ slackFormat: e.target.value as 'markdown' | 'plain' })}
            >
              <option value="markdown">Markdown</option>
              <option value="plain">Plain</option>
            </select>
          </div>
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">Mention</label>
          <input
            data-patch-key="slackMention"
            className="workflow-action-panel__input"
            value={d.slackMention}
            onChange={(e) => patch({ slackMention: e.target.value })}
            placeholder="@channel"
          />
        </div>
        <div className="workflow-action-panel__collapse">
          <button
            type="button"
            className="workflow-action-panel__collapse-btn"
            onClick={() => patch({ slackBlocksOpen: !d.slackBlocksOpen })}
          >
            {d.slackBlocksOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Block Kit
          </button>
          {d.slackBlocksOpen && <p className="workflow-action-panel__hint">Visual block builder can connect here later.</p>}
        </div>
      </>
    );
  } else if (kind === 'update-crm') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Entity</label>
          <select
            className="workflow-action-panel__select"
            value={d.crmEntity}
            onChange={(e) => patch({ crmEntity: e.target.value })}
          >
            {['Contact', 'Lead', 'Account', 'Deal', 'Custom'].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Record ID</label>
          <input
            data-patch-key="crmRecordId"
            className="workflow-action-panel__input"
            value={d.crmRecordId}
            onChange={(e) => patch({ crmRecordId: e.target.value })}
          />
        </div>
        {renderKv('Fields to update', d.crmFields, (next) => patch({ crmFields: next }), 'crmFieldValue')}
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">
            <input
              type="checkbox"
              checked={d.crmCreateIfMissing}
              onChange={(e) => patch({ crmCreateIfMissing: e.target.checked })}
            />{' '}
            Create if not exists
          </label>
          <label className="workflow-action-panel__label">
            <input
              type="checkbox"
              checked={d.crmTriggerAutomations}
              onChange={(e) => patch({ crmTriggerAutomations: e.target.checked })}
            />{' '}
            Trigger CRM automations
          </label>
        </div>
      </>
    );
  } else if (kind === 'emit-event') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Event name</label>
          <input
            data-patch-key="emitEventName"
            className="workflow-action-panel__input"
            value={d.emitEventName}
            onChange={(e) => patch({ emitEventName: e.target.value })}
          />
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">Payload</label>
          <textarea
            data-patch-key="emitPayload"
            className="workflow-action-panel__textarea"
            value={d.emitPayload}
            onChange={(e) => patch({ emitPayload: e.target.value })}
          />
        </div>
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">Event listeners</div>
          <p className="workflow-action-panel__hint">Downstream workflows and webhooks registered for this event will receive the payload.</p>
        </div>
      </>
    );
  } else if (kind === 'create-record') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Entity</label>
          <select
            className="workflow-action-panel__select"
            value={d.createEntity}
            onChange={(e) => patch({ createEntity: e.target.value })}
          >
            {['Contact', 'Lead', 'Account', 'Deal', 'Custom'].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>
        {renderKv('Fields', d.createFields, (next) => patch({ createFields: next }), 'createFieldValue')}
      </>
    );
  } else if (kind === 'delete-record') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Entity</label>
          <select
            className="workflow-action-panel__select"
            value={d.deleteEntity}
            onChange={(e) => patch({ deleteEntity: e.target.value })}
          >
            {['Contact', 'Lead', 'Account', 'Deal', 'Custom'].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Record ID</label>
          <input
            data-patch-key="deleteRecordId"
            className="workflow-action-panel__input"
            value={d.deleteRecordId}
            onChange={(e) => patch({ deleteRecordId: e.target.value })}
          />
        </div>
      </>
    );
  } else if (kind === 'logger') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">Level</label>
          <select
            className="workflow-action-panel__select"
            value={d.logLevel}
            onChange={(e) => patch({ logLevel: e.target.value as FlowActionPersistedData['logLevel'] })}
          >
            {(['debug', 'info', 'warn', 'error'] as const).map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Message</label>
          <textarea
            data-patch-key="logMessage"
            className="workflow-action-panel__textarea workflow-action-panel__textarea--sm"
            value={d.logMessage}
            onChange={(e) => patch({ logMessage: e.target.value })}
          />
        </div>
      </>
    );
  } else if (kind === 'wait') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label">Delay type</label>
          <div className="workflow-action-panel__method-toggle">
            {(['duration', 'until', 'condition'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`workflow-action-panel__method-btn${d.waitDelayType === t ? ' workflow-action-panel__method-btn--on' : ''}`}
                onClick={() => patch({ waitDelayType: t })}
              >
                {t === 'duration' ? 'Duration' : t === 'until' ? 'Until' : 'Condition'}
              </button>
            ))}
          </div>
        </div>
        {d.waitDelayType === 'duration' && (
          <div className="workflow-action-panel__section">
            <label className="workflow-action-panel__label workflow-action-panel__label--req">Duration</label>
            <div className="workflow-action-panel__row">
              <input
                data-patch-key="waitDuration"
                className="workflow-action-panel__input"
                value={d.waitDuration}
                onChange={(e) => patch({ waitDuration: e.target.value })}
              />
              <select
                className="workflow-action-panel__select"
                style={{ width: 120 }}
                value={d.waitUnit}
                onChange={(e) => patch({ waitUnit: e.target.value as FlowActionPersistedData['waitUnit'] })}
              >
                <option value="seconds">seconds</option>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </div>
          </div>
        )}
        {d.waitDelayType === 'until' && (
          <div className="workflow-action-panel__section">
            <div className="workflow-action-panel__section-title">Until</div>
            <label className="workflow-action-panel__label">Time</label>
            <div className="workflow-action-panel__row">
            <input
              data-patch-key="waitUntilTime"
              className="workflow-action-panel__input"
              value={d.waitUntilTime}
              onChange={(e) => patch({ waitUntilTime: e.target.value })}
            />
              <select
                className="workflow-action-panel__select"
                style={{ width: 100 }}
                value={d.waitUntilTz}
                onChange={(e) => patch({ waitUntilTz: e.target.value })}
              >
                <option value="UTC">UTC</option>
                <option value="local">Local</option>
              </select>
            </div>
            <label className="workflow-action-panel__label">Date</label>
            <input
              data-patch-key="waitUntilDateLabel"
              className="workflow-action-panel__input"
              value={d.waitUntilDateLabel}
              onChange={(e) => patch({ waitUntilDateLabel: e.target.value })}
              placeholder="Next weekday"
            />
          </div>
        )}
        {d.waitDelayType === 'condition' && (
          <div className="workflow-action-panel__section">
            <label className="workflow-action-panel__label workflow-action-panel__label--req">Condition</label>
            <input
              data-patch-key="waitCondition"
              className="workflow-action-panel__input"
              value={d.waitCondition}
              onChange={(e) => patch({ waitCondition: e.target.value })}
            />
            <label className="workflow-action-panel__label">Timeout (hours)</label>
            <input
              data-patch-key="waitConditionTimeoutHours"
              className="workflow-action-panel__input"
              value={d.waitConditionTimeoutHours}
              onChange={(e) => patch({ waitConditionTimeoutHours: e.target.value })}
            />
          </div>
        )}
      </>
    );
  } else if (kind === 'condition-check') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <label className="workflow-action-panel__label workflow-action-panel__label--req">Condition</label>
          <textarea
            data-patch-key="conditionExpr"
            className="workflow-action-panel__textarea workflow-action-panel__textarea--sm"
            value={d.conditionExpr}
            onChange={(e) => patch({ conditionExpr: e.target.value })}
          />
          <p className="workflow-action-panel__hint">Branches true/false can be wired from this node in a future flow-control release.</p>
        </div>
      </>
    );
  } else if (kind === 'html-viewer') {
    body = (
      <>
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">HTML content</div>
          <p className="workflow-action-panel__hint">
            Content type is text/html only: use HTML markup and <kbd>{'{{ }}'}</kbd> variables. Markdown, plain text, and
            other formats are not accepted.
          </p>
          <label className="workflow-action-panel__label">Markup</label>
          <MustacheSuggestTextarea
            resetKey={node.id}
            id={`html-viewer-${node.id}`}
            data-patch-key="htmlViewerHtml"
            className="workflow-action-panel__textarea"
            rows={12}
            value={d.htmlViewerHtml ?? ''}
            onChange={(next) => patch({ htmlViewerHtml: next })}
            suggestions={htmlViewerSuggestions}
            classNames={MUSTACHE_SUGGEST_CN_ACTION}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </>
    );
  }

  return (
    <aside className={`workflow-action-panel${kind === 'emit-event' ? ' workflow-action-panel--emit' : ''}`}>
      <div className="workflow-action-panel__head">
        <div>
          <h2 className="workflow-action-panel__title">{title}</h2>
          <p className="workflow-action-panel__subtitle">
            {v.level === 'error' ? '✗ Fix required fields to run.' : v.level === 'warning' ? '⚠ Optional fields empty.' : '✓ Valid'}
          </p>
        </div>
        <span className="workflow-action-panel__badge">Action</span>
        <div className="workflow-action-panel__head-actions">
          {onTogglePanelMaximize ? (
            <WorkflowConfigPanelMaximizeButton expanded={panelMaximized} onToggle={onTogglePanelMaximize} />
          ) : null}
          <button type="button" className="workflow-action-panel__close" onClick={onClose} aria-label="Close panel">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="workflow-action-panel__section">
        <label className="workflow-action-panel__label">Node name</label>
        <input
          className="workflow-action-panel__input"
          value={d.nodeName}
          onChange={(e) => patch({ nodeName: slugifyActionNodeName(e.target.value) })}
        />
      </div>

      {upstreamHints.length > 0 && (
        <div className="workflow-action-panel__section">
          <div className="workflow-action-panel__section-title">Insert variable</div>
          <div className="workflow-action-panel__vars">
            {upstreamHints.slice(0, 24).map((h) => (
              <button key={h} type="button" className="workflow-action-panel__chip" onClick={() => insertVar(h)}>
                {h.length > 28 ? `${h.slice(0, 26)}…` : h}
              </button>
            ))}
          </div>
          <button type="button" className="workflow-action-panel__chip" onClick={() => insertVar('{{ }}')}>
            Empty {'{{ }}'}
          </button>
        </div>
      )}

      {body}

      <div className="workflow-action-panel__section">
        <div className="workflow-action-panel__section-title">Output</div>
        <pre className="workflow-action-panel__outputs">{outputs}</pre>
      </div>

      <div className="workflow-action-panel__footer">
        <button type="button" className="workflow-action-panel__btn-danger" onClick={() => onDelete(node.id)}>
          Delete node
        </button>
      </div>
    </aside>
  );
}
